// © 2025 Emmanuel Takyi Obodai — All rights reserved
import React, { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Link,
} from "react-router-dom";

import OfflineScreen from "./components/OfflineScreen";
import ScrollToTopButton from "./components/ScrollToTopButton";
import CookieModal from "./components/CookieModal";
import NewHeader from "./components/NewHeader";
import NewFooter from "./components/NewFooter";
import FallbackCourseNotAvailable from "./components/FallbackCourseNotAvaliable";
import StatusGate from "./components/StatusGate";
import Home from "./pages/homepg/home";
import TestHomePage from "./pages/TestHome/TestHomePage";
import TestHomeLayout from "./pages/TestHome/TestHomeLayout";
import Login from "./pages/login/login";
import ScrollToTop from "./ScrollToTop";
import Admissions from "./pages/admission/admissions";
import OurInstitution from "./pages/Our Institution/our-institution";
import ExecutiveTeam from "./pages/Executive Team/ExecutiveTeam";
import ExecutivePage from "./pages/Executive Team/ExecutivePage";
import NotFoundPage from "./pages/NotFoundPage";
import Accreditation from "./pages/aboutus/accreditation/Accreditation";
import RegisterYourInterestForm from "./components/RegisterYourInterestForm";
import MsgFrmThePresident from "./pages/aboutus/MsgFrmThePresident";
import ExecutiveSummerSchool from "./pages/academicprogrammes/ExecutiveSummerSchool/ExecutiveSummerSchool";
import LondonExecutiveWeek from "./london-executive-week/LondonExecutiveWeek";
import FacultyPage from "./pages/faculties/FacultyPage";
import CertifiedCoursesTemplate from "./pages/academicprogrammes/certifiedCourses/certifiedCoursesTemplate";
import CertifiedPage from "./pages/CertifiedPage/CertifiedPage";
import MasterProgrammesTemplate from "./pages/academicprogrammes/MastersProgramme/masterProgrammesTemplate";
// import BachelorsDegreeTemplate from "./pages/academicprogrammes/BachelorsDegree/BachelorsDegreeTemplate";
import ExecutiveEducationTemplate from "./pages/academicprogrammes/ExecutiveEducation/executiveEducationTemplate";
import ExecutiveGovernmentPage from "./pages/academicprogrammes/EEForGovermentOfficials/ExecutiveGovernmentPage";
import NewsPage from "./pages/news/NewsPage";
import StudentComplaintForm from "./pages/StudentComplaintForm/StudentComplaintForm";
import RecruitementAgentForm from "./pages/RecruitementAgentForm/RecruitementAgentForm";
import TeachingStaffHomePage from "./pages/TeachingStaff/TeachingStaffHomePage";
import SingleTeacherPage from "./pages/TeachingStaff/SingleTeacherPage";
import StrategicAmbitionPage from "./pages/StrategicAmbition/StrategicAmbitionPage";
import CareersPage from "./pages/CareerPage/CareersPage";
import SCPPage from "./pages/StudyCentrePartner/SCPPage";
import CorporatePartnershipPage from "./pages/CorporatePartnership/CorporatePartnershipPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicy/PrivacyPolicyPage";

function Layout({ children }) {
  return (
    <div className="select-none bg-sdbodywhite">
      <NewHeader />
      <ScrollToTop />
      <main className="pt-[68px] min-[1073px]:pt-[76px]">{children}</main>
      <NewFooter />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const state = location.state || {};

  return (
    <>
      <Routes location={state?.backgroundLocation || location}>
        <Route
          path="/"
          element={
            <TestHomeLayout>
              <TestHomePage />
            </TestHomeLayout>
          }
        />

        <Route
          path="/login"
          element={
            <Layout>
              <Login />
            </Layout>
          }
        />
        <Route
          path="/admissions"
          element={
            <Layout>
              <Admissions />
            </Layout>
          }
        />
        <Route
          path="/about/our-institution"
          element={
            <Layout>
              <OurInstitution />
            </Layout>
          }
        />
        <Route
          path="/about/message-president"
          element={
            <Layout>
              <MsgFrmThePresident />
            </Layout>
          }
        />

        <Route
          path="/student-complaint-form"
          element={
            <Layout>
              <StudentComplaintForm />
            </Layout>
          }
        />

        <Route
          path="/recruitment-agent-form"
          element={
            <Layout>
              <RecruitementAgentForm />
            </Layout>
          }
        />

        <Route
          path="/register-your-interest"
          element={
            <Layout>
              <div>
                <div
                  className="bg-cover bg-center py-10"
                  style={{
                    backgroundImage:
                      "url('https://images.unsplash.com/photo-1603573355706-3f15d98cf100?q=80&w=1929&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
                  }}
                >
                  <RegisterYourInterestForm />
                </div>
              </div>
            </Layout>
          }
        />

        <Route
          path="/about/teaching-staff"
          element={
            <Layout>
              <TeachingStaffHomePage />
            </Layout>
          }
        />

        <Route
          path="/about/teaching-staff/:teachingStaffId"
          element={
            <Layout>
              <SingleTeacherPage />
            </Layout>
          }
        />

        <Route
          path="/about/strategic-ambition"
          element={
            <Layout>
              <StrategicAmbitionPage />
            </Layout>
          }
        />

        {/* Career Page */}
        <Route
          path="/career"
          element={
            <Layout>
              <CareersPage />
            </Layout>
          }
        />

        <Route
          path="/about/executive-team"
          element={
            <Layout>
              <ExecutiveTeam />
            </Layout>
          }
        />
        <Route
          path="/about/executive-team/:executiveId"
          element={
            <Layout>
              <ExecutivePage />
            </Layout>
          }
        />
        <Route
          path="/faculties/:facultyName"
          element={
            <Layout>
              <FacultyPage />
            </Layout>
          }
        />
        <Route
          path="/about/accreditation"
          element={
            <Layout>
              <Accreditation />
            </Layout>
          }
        />

        <Route
          path="/study-centre-partner"
          element={
            <Layout>
              <SCPPage />
            </Layout>
          }
        />

        <Route
          path="/corporate-partnership"
          element={
            <Layout>
              <CorporatePartnershipPage />
            </Layout>
          }
        />

        <Route
          path="/privacy-policy"
          element={
            <Layout>
              <PrivacyPolicyPage />
            </Layout>
          }
        />

        <Route
          path="*"
          element={
            <Layout>
              <NotFoundPage />
            </Layout>
          }
        />
        <Route
          path="/certified"
          element={
            <Layout>
              <CertifiedPage />
            </Layout>
          }
        />
        <Route
          path="/certified/:certifiedCourse"
          element={
            <Layout>
              <CertifiedCoursesTemplate />
            </Layout>
          }
        />
        <Route
          path="/masters/:masterProgramme"
          element={
            <Layout>
              <MasterProgrammesTemplate />
            </Layout>
          }
        />
        {/* <Route
          path="/bachelors/:bachelorsDegreeCourse"
          element={
            <Layout>
              <BachelorsDegreeTemplate />
            </Layout>
          }
        /> */}
        <Route
          path="/executive/:courseId"
          element={
            <Layout>
              <ExecutiveEducationTemplate />
            </Layout>
          }
        />
        <Route
          path="/executive-government/:id"
          element={
            <Layout>
              <ExecutiveGovernmentPage />
            </Layout>
          }
        />
        <Route
          path="/executive-summerschool"
          element={
            <Layout>
              <ExecutiveSummerSchool />
            </Layout>
          }
        />
        <Route
          path="/london-executive-week"
          element={<LondonExecutiveWeek />}
        />
        <Route
          path="/news/:newsId"
          element={
            <Layout>
              <NewsPage />
            </Layout>
          }
        />

        <Route
          path="/pagenotopenyet"
          element={
            <Layout>
              <FallbackCourseNotAvailable
                onClose={() => window.history.back()}
              />
            </Layout>
          }
        />
      </Routes>

      {state?.backgroundLocation && (
        <Routes>
          <Route
            path="/pagenotopenyet"
            element={
              <FallbackCourseNotAvailable
                onClose={() => window.history.back()}
              />
            }
          />
        </Routes>
      )}
    </>
  );
}

export default function App() {
  const endpoint =
    "https://tws.globara.uk/api/project-status/sapphirediplomacy";

  return (
    <HelmetProvider>
      <StatusGate
        endpoint={endpoint}
        ok={
          <>
            <AppContent />
            <ScrollToTopButton />
            <CookieModal />
          </>
        }
        brokenUI={() => <OfflineScreen />}
        errorPolicy="failOpen"
      />
    </HelmetProvider>
  );
}
